// react and js
import React, { useState } from "react";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import * as Yup from "yup";

// mui
import { DialogTitle, DialogContent, DialogActions, Typography, IconButton, Box, Link } from "@mui/material";

// components
import ModalDialog from "../../../components/ModalDialog";
import Iconify from "../../../components/Iconify";
import backendRequest from "../../../components/BackendRequest";
import SmartParameter from "../../../components/SmartParameter";

// theme
import { ModalActionsLoadingButton, ModalContentTextField, ModalAlertSuccess, ModalAlertError } from "../../../theme/styled";

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export default function HandleNewsletter({ open, onClose, storageKey }) {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  HandleNewsletter.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    storageKey: PropTypes.string,
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().email("Please enter a valid email address").required("Email address is required").max(255, "Email address is too long"),
    }),
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      setSubmitStatus(null);
      setErrorMessage("");

      // Prepare the data for submission
      const sendData = {
        request_type: "forms",
        secret: "bff43eeb8f5ef8506a241c3724a259c0",
        route_info: 4007,
        "form-email": values.email,
        "ip-location": "55ce343b9ec169b03c5123807bf246a4",
      };

      backendRequest(`${process.env.REACT_APP_URL_API_FORMS}`, sendData)
        .then((contentFromBackend) => {
          const keyOnly = Object.keys(contentFromBackend);
          const checkKey = keyOnly.includes("message");

          if (checkKey === true) {
            if (contentFromBackend.message === "ok") {
              formik.resetForm();
              setSubmitStatus("success");
              setSubmitting(false);
              
              // Save subscription status to localStorage
              if (storageKey) {
                const subscriptionData = {
                  subscribed: true,
                  timestamp: Date.now()
                };
                localStorage.setItem(storageKey, JSON.stringify(subscriptionData));
              }
            } else {
              setSubmitStatus("error");
              setErrorMessage("Something went wrong. Please try again.");
            }
          } else {
            setSubmitStatus("error");
            setErrorMessage("Something went wrong. Please try again.");
          }
          setSubmitting(false);
        })
        .catch(() => {
          setSubmitStatus("error");
          setErrorMessage("Something went wrong. Please try again.");
        });
    },
  });

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps } = formik;

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClose = () => {
    if (!isSubmitting) {
      // Save "Maybe Later" status to localStorage if not already successfully subscribed
      if (storageKey && submitStatus !== "success") {
        const dismissData = {
          subscribed: false,
          dismissed: true,
          timestamp: Date.now()
        };
        localStorage.setItem(storageKey, JSON.stringify(dismissData));
      }
      
      formik.resetForm();
      setSubmitStatus(null);
      setErrorMessage("");
      onClose();
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleSubscribe = () => {
    handleSubmit();
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  let saveAndCloseDisabled = false;

  const hasValidationErrors = () => Object.keys(formik.errors).length > 0;
  if (hasValidationErrors()) {
    saveAndCloseDisabled = true;
  }
  if (values.email === "") {
    saveAndCloseDisabled = true;
  }

  // ----------------------------------------------------------------------
  // Main component
  // ----------------------------------------------------------------------
  return (
    <ModalDialog
      open={open}
      handleClose={handleClose}
      scroll="paper"
      outlet={
        <>
          <DialogTitle
            id="newsletter-dialog-title"
            sx={{
              background: "#F2F2F2",
              textTransform: "none",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              alignContent: "center",
              py: 1,
              pl: 2,
              pr: 1,
            }}
          >
            <Typography variant="contentBig" sx={{ fontWeight: 600 }}>
              Don’t miss out on new innovations
            </Typography>

            <IconButton onClick={handleClose} sx={{ borderRadius: "10px" }} disabled={isSubmitting}>
              <Iconify icon="eva:close-outline" minWidth={25} minHeight={25} color="#2E2E2E" />
            </IconButton>
          </DialogTitle>

          <DialogContent
            dividers
            sx={{
              background: "#F2F2F2",
              borderTop: "1px solid #C8C8C8",
              borderBottom: "0px solid #C8C8C8",
              p: 0,
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <Box sx={{
              display: "flex",
              minHeight: "300px",
              flexDirection: { xs: "column", sm: "row" },
            }}>
              {/* Top/Left side - Image */}
              <Box
                sx={{
                  flex: { xs: "0 0 auto", sm: "0 0 50%" },
                  px: { xs: 3, sm: 2 },
                  pt: 2,
                  pr: { xs: 3, sm: 4 },
                  pl: { xs: 3, sm: 4 },
                  pb: { xs: 0, sm: 0 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    flex: { xs: "0 0 auto", sm: "0 0 50%" },
                    backgroundImage: `url(/illustrations/misc/newsletter.png)`,
                    backgroundSize: "contain",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    minHeight: { xs: "150px", sm: "200px" },
                    maxHeight: { xs: "150px", sm: "200px" },
                    backgroundColor: "#F2F2F2",
                  }}
                />
              </Box>

              {/* Bottom/Right side - Content */}
              <Box
                sx={{
                  flex: { xs: "1", sm: "0 0 50%" },
                  pr: { xs: 3, sm: 2 },
                  pl: { xs: 3, sm: 0.5 },
                  pt: { xs: 1, sm: 2 },
                  pb: { xs: 2, sm: 0 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="contentBig"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Get exclusive news about:
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="contentMiddle" sx={{ color: "#424242", mb: 1 }}>
                    • New Expert Advisor Builder features
                    <br />
                  </Typography>
                  <Typography variant="contentMiddle" sx={{ color: "#424242", mb: 1 }}>
                    • Regular market analysis and insights
                    <br />
                  </Typography>
                  <Typography variant="contentMiddle" sx={{ color: "#424242", mb: 1 }}>
                    • Trading strategies and tips
                    <br />
                  </Typography>
                  <Typography variant="contentMiddle" sx={{ color: "#424242", mb: 1 }}>
                    • Monthly MT5 Tick Data updates
                    <br />
                  </Typography>
                </Box>

                {/* ---------------------------------------------------------------------- */}
                {/* Parameter configuration */}
                {/* Email Input */}
                {/* ---------------------------------------------------------------------- */}
                <Box sx={{ mb: 1 }}>
                  <SmartParameter
                    value="Email Address"
                    helpReference=""
                    helpText=""
                    helpLink=""
                    fieldName="email"
                    touched={touched}
                    errors={errors}
                  />
                  <ModalContentTextField
                    {...getFieldProps("email")}
                    fullWidth
                    size="small"
                    type="email"
                    disabled={isSubmitting}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>

                <Typography
                  variant="contentTiny"
                  sx={{
                    fontStyle: "italic",
                  }}
                >
                  We respect your privacy. Unsubscribe at any time.
                </Typography>

                {/* Success Message */}
                {submitStatus === "success" && (
                  <Box sx={{ mt: 1 }}>
                    <ModalAlertSuccess
                      sx={{
                        m: "0 !important",
                      }}
                      severity="success"
                    >
                      Successfully subscribed!
                    </ModalAlertSuccess>
                  </Box>
                )}

                {/* Error Message */}
                {submitStatus === "error" && (
                  <Box sx={{ mt: 1 }}>
                    <ModalAlertError
                      sx={{
                        m: "0 !important",
                      }}
                      severity="error"
                    >
                      {errorMessage}
                    </ModalAlertError>
                  </Box>
                )}
              </Box>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              background: "#F2F2F2",
              p: 2,
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "column",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <Box sx={{ width: "100%", display: "flex", gap: 1 }}>
              <ModalActionsLoadingButton fullWidth size="large" onClick={handleClose} disableRipple={Boolean(true)} sx={{ p: 1 }}>
                 {submitStatus === "success" ? "Close" : "Maybe Later"}
              </ModalActionsLoadingButton>

              <ModalActionsLoadingButton
                fullWidth
                size="large"
                onClick={handleSubscribe}
                disabled={saveAndCloseDisabled}
                disableRipple={Boolean(true)}
                sx={{ p: 1 }}
                loading={isSubmitting}
              >
                {isSubmitting ? null : "Subscribe Now"}
              </ModalActionsLoadingButton>
            </Box>
          </DialogActions>
        </>
      }
    />
  );
}
